import Webcam from "react-webcam";
import "./styles.css";
import { RiveAnimator } from "../RiveAnimator/RiveAnimator";
import { FaceRotationFilename, FrontCamConstraints } from "../../constants";
import {
  checkFrontFace,
  checkFaceRotationFromTarget,
  videoToCoverCanvasCoordinates,
  getTargetPosition,
} from "./utilities";
import { useRef, useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useOpenCv } from "opencv-react";
import { checkLightQuality } from "./lightEstimate";
import { isTablet } from "react-device-detect";
import { useConfig } from "../../context/ConfigContext";
import { ScanGuidance } from "./ScanGuidance/ScanGuidance";

export const FaceScan = ({
  sideFaceEnabled,
  runFaceModel,
  addFrontImage,
  addSideImage,
  onComplete,
  onError,
  onExitClick,
}) => {
  const { t } = useTranslation("scanModule");
  const { cv } = useOpenCv();

  const { configData } = useConfig();

  const camContainerRef = useRef(null);
  const webcamRef = useRef(null);
  // Web cam crop
  const [webcamStyle, setWebcamStyle] = useState({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center center",
    transform: "scale(1)",
    transformOrigin: "center center",
  });

  const zoomLevels = [1, 1.5, 2];
  const [zoomLevel, setZoomLevel] = useState(1);
  const zoomLevelRef = useRef(zoomLevel);

  const canvasRef = useRef(null);
  const maskRef = useRef(null);

  const maskBounds = useRef([0, 0, 0, 0]);
  const coverImageCoords = useRef([0, 0, 0, 0]);

  // Face capture parameters
  const [cameraLoaded, setCameraLoaded] = useState(false);
  const [showGuidanceOverlay, setShowGuidanceOverlay] = useState(true);
  const [frontFaceAligned, setFrontFaceAligned] = useState(false);
  const [lightQuality, setLightQuality] = useState("dark");
  const [scanStep, setScanStep] = useState(1);

  // CSS Control parameters
  const [showCaptureFlash, setShowCaptureFlash] = useState(false);
  const isAnimationEnded = useRef(false);

  const resetCanvas = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const setMaskBounds = () => {
    const { x, y, width, height } = maskRef.current.getBoundingClientRect();
    maskBounds.current = [x, y, width, height];
  };

  const setCanvasOverlaySize = () => {
    const videoElement = webcamRef.current.video;

    // Update canvas dimensions - Explicitly not defined in CSS
    canvasRef.current.width = videoElement.offsetWidth;
    canvasRef.current.height = canvasRef.current.offsetHeight;

    // Get Image Coordinates for "objectFit: cover" property
    const containerRect = camContainerRef.current.getBoundingClientRect();
    const scaleX = containerRect.width / videoElement.videoWidth;
    const scaleY = containerRect.height / videoElement.videoHeight;
    const scale = Math.max(scaleX, scaleY);
    const actualWidth = videoElement.videoWidth * scale;
    const actualHeight = videoElement.videoHeight * scale;
    const actualXOffset = Math.round(
      Math.max((actualWidth - containerRect.width) / 2, 0),
    );
    const actualYOffset = Math.round(
      Math.max((actualHeight - containerRect.height) / 2, 0),
    );

    coverImageCoords.current = [
      actualXOffset,
      actualYOffset,
      actualWidth,
      actualHeight,
    ];
  };

  const updateAnimationState = () => {
    isAnimationEnded.current = true;
  };

  const cropWebCam = (zoom) => {
    setZoomLevel(zoom);

    setWebcamStyle((prevStyle) => ({
      ...prevStyle,
      transform: `scale(${zoom})`,
    }));
  };

  const adjustCanvasSize = (canvasRef, width, height) => {
    const prevWidth = canvasRef.current.width;
    const prevHeight = canvasRef.current.height;
    canvasRef.current.width = width;
    canvasRef.current.height = height;

    return { prevWidth, prevHeight };
  };

  const drawZoomedFrame = (ctx, video, scale, canvasWidth, canvasHeight) => {
    const zoomedWidth = video.videoWidth * scale;
    const zoomedHeight = video.videoHeight * scale;
    const offsetX = (video.videoWidth - zoomedWidth) / 2;
    const offsetY = (video.videoHeight - zoomedHeight) / 2;

    ctx.canvas.width = zoomedWidth;
    ctx.canvas.height = zoomedHeight;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(
      video,
      offsetX,
      offsetY,
      zoomedWidth,
      zoomedHeight,
      0,
      0,
      zoomedWidth,
      zoomedHeight,
    );
  };

  const saveCanvasAsImage = (canvasRef, filename) => {
    const imageSrc = canvasRef.current.toDataURL("image/jpeg");
    addSideImage(filename, imageSrc);
  };

  const resetCanvasSize = (canvasRef, prevWidth, prevHeight) => {
    canvasRef.current.width = prevWidth;
    canvasRef.current.height = prevHeight;
  };

  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  const runAIDetection = async () => {
    let faceSidePos = 0;
    let scanStep = 1;
    let captureTimer = null,
      sideDelayTimer = null;

    let targetPosition = { x: 0, y: 0 };
    let centeredNosePosition = null;

    // Animate head motion guide
    let currentGuidePosition = { x: 0 };
    let guideAnimationVelocity = 1;
    let resetGuide = true;

    try {
      while (scanStep < 3) {
        if (isAnimationEnded.current === false) {
          await new Promise((resolve) =>
            requestAnimationFrame(() => resolve()),
          );
          continue;
        }

        const video = webcamRef.current.video;
        if (!video) break;

        const faces = await runFaceModel(video);

        const canvasFaceCoords = videoToCoverCanvasCoordinates(
          faces,
          canvasRef.current.width,
          canvasRef.current.height,
          video.videoWidth,
          video.videoHeight,
          coverImageCoords.current[2],
          coverImageCoords.current[3],
          coverImageCoords.current[0],
          coverImageCoords.current[1],
          zoomLevelRef.current,
        );

        if (canvasFaceCoords !== null) {
          // Clean up canvas
          const ctx = canvasRef.current.getContext("2d");
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

          // For visualization of face detection
          // drawDetection(canvasFaceCoords, ctx);

          switch (scanStep) {
            case 1:
              // Test effect of ctx drawImage
              const lightQuality = checkLightQuality(
                cv,
                video,
                faces[0],
                configData?.lightSensitivity,
              );
              setLightQuality(lightQuality);

              const distanceSuccess = checkFrontFace(
                canvasFaceCoords,
                true,
                maskBounds.current[0],
                maskBounds.current[1],
                maskBounds.current[2],
                maskBounds.current[3],
              );
              setFrontFaceAligned(distanceSuccess);

              if (
                lightQuality === "good" &&
                distanceSuccess &&
                captureTimer === null
              ) {
                captureTimer = Date.now();
              }

              if (!distanceSuccess || lightQuality !== "good") {
                captureTimer = null;
              }

              // Step complete
              if (captureTimer !== null && Date.now() - captureTimer >= 800) {
                // Save this image and send it later
                const videoWidth = video.videoWidth;
                const videoHeight = video.videoHeight;

                const { prevWidth, prevHeight } = adjustCanvasSize(
                  canvasRef,
                  videoWidth,
                  videoHeight,
                );

                drawZoomedFrame(
                  ctx,
                  video,
                  1 / zoomLevelRef.current,
                  canvasRef.current.width,
                  canvasRef.current.height,
                );
                addFrontImage(
                  canvasRef.current.toDataURL("image/jpeg"),
                  canvasFaceCoords.allKeypoints,
                  canvasRef.current.width,
                  canvasRef.current.height,
                );

                resetCanvasSize(canvasRef, prevWidth, prevHeight);

                const noseIndex = canvasFaceCoords.keypoints.findIndex(
                  (item) => item.name === "noseTip",
                );
                centeredNosePosition = canvasFaceCoords.keypoints[noseIndex];
                setScanStep((prevValue) => prevValue + 1);
                scanStep += sideFaceEnabled ? 1 : 2;
                captureTimer = null;
              }
              break;
            case 2:
              // Check face side position to go to next step
              if (faceSidePos > 1) {
                scanStep = 3;
                break;
              }

              // Get target position of face scan
              targetPosition = getTargetPosition(
                faceSidePos,
                ctx,
                centeredNosePosition,
              );

              // get current position of face through nosetip
              const index = canvasFaceCoords.keypoints.findIndex(
                (item) => item.name === "noseTip",
              );
              const currentPosition = canvasFaceCoords.keypoints[index];

              // When reset guide is on, define guideline's movement velocity
              if (resetGuide) {
                guideAnimationVelocity =
                  currentPosition.x - targetPosition.x > 0 ? -1 : 1;
                currentGuidePosition.x = currentPosition.x;
                currentGuidePosition.y = targetPosition.y;

                resetGuide = false;
              }

              // Check face position is matched or not
              if (sideDelayTimer === null) {
                let positionMatched = false;
                positionMatched = checkFaceRotationFromTarget(
                  targetPosition,
                  currentPosition,
                  currentGuidePosition,
                  ctx,
                );

                if (positionMatched) {
                  if (captureTimer === null) captureTimer = Date.now();
                } else {
                  captureTimer = null;
                }
              }

              // Do guide animation
              currentGuidePosition.x =
                currentGuidePosition.x + guideAnimationVelocity;

              if (
                Math.abs(currentGuidePosition.x - targetPosition.x) < 5 ||
                Math.abs(currentPosition.x - targetPosition.x) < 2
              ) {
                resetGuide = true;
              }

              // Clean-up canvas
              if (sideDelayTimer === null) {
                // Hold side position - 200ms time
                if (captureTimer && Date.now() - captureTimer >= 200) {
                  setShowCaptureFlash(true);

                  // Save the image and send it later
                  const videoWidth = video.videoWidth;
                  const videoHeight = video.videoHeight;

                  const { prevWidth, prevHeight } = adjustCanvasSize(
                    canvasRef,
                    videoWidth,
                    videoHeight,
                  );

                  drawZoomedFrame(
                    ctx,
                    video,
                    1 / zoomLevelRef.current,
                    canvasRef.current.width,
                    canvasRef.current.height,
                  );

                  saveCanvasAsImage(
                    canvasRef,
                    `${FaceRotationFilename[faceSidePos]}`,
                  );

                  resetCanvasSize(canvasRef, prevWidth, prevHeight);

                  sideDelayTimer = Date.now();
                }
              }

              // Wait for flash to complete - 110ms animation
              if (sideDelayTimer && Date.now() - sideDelayTimer >= 110) {
                faceSidePos += 1;
                captureTimer = null;
                resetGuide = true;
                sideDelayTimer = null;
                setShowCaptureFlash(false);
              }
              break;
            default:
              break;
          }
        }

        await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      }
      resetCanvas();
      onComplete();
    } catch (error) {
      console.error(error);
      onError();
      return;
    }
  };

  return (
    <>
      <div ref={camContainerRef} className="webcam">
        <Webcam
          audio={false}
          mirrored={true}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={FrontCamConstraints}
          style={webcamStyle}
          forceScreenshotSourceSize={true}
          imageSmoothing={true}
          onUserMedia={() => {
            if (configData?.ScanGuidancePageFlags?.isEnabled) {
              setCameraLoaded(true);
            } else {
              const timerId = setTimeout(() => {
                setCameraLoaded(true);
              }, 500);
              return () => clearTimeout(timerId);
            }
          }}
          onLoadedMetadata={() => setCanvasOverlaySize()}
          onUserMediaError={() => onError()}
        />
      </div>

      {cameraLoaded && showGuidanceOverlay && (
        <ScanGuidance
          onContinue={() => {
            setShowGuidanceOverlay(false);
            runAIDetection();
          }}
        />
      )}

      {!cameraLoaded && (
        <div className="scan-module-loading">
          <CircularProgress
            disableShrink
            color="inherit"
            size={40}
            thickness={4}
          />
          <p className="scan-interim-text">{t("start-camera")}</p>
        </div>
      )}
      <div className="detection-canvas">
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",
          }}
        />
      </div>
      {cameraLoaded && !showGuidanceOverlay && scanStep === 1 && (
        <>
          <div className="scan-mask">
            <img
              ref={maskRef}
              src="FaceMask_Green.svg"
              width={100}
              alt="Face Position"
              style={{
                width: "100%",
                height: "100%",
                maxWidth: "400px",
                maxHeight: "400px",
                opacity: frontFaceAligned && lightQuality === "good" ? 1 : 0,
              }}
              onLoad={() => setMaskBounds()}
            />
          </div>
          <div className="scan-mask">
            <div
              style={{
                width: "100%",
                height: "100%",
                maxWidth: "400px",
                maxHeight: "400px",
                opacity: frontFaceAligned && lightQuality === "good" ? 0 : 1,
              }}
            >
              <RiveAnimator
                fileName="FaceMask_Animation.riv"
                animationName="Animation"
                onAnimationEnd={updateAnimationState}
              />
            </div>
          </div>
          <div className="face-scan-header">
            <div className="status-tiles-centered">
              <div className={`tile ${frontFaceAligned ? "success" : ""}`}>
                <h4 className="tile-title">{t("face-position")}</h4>
                <p className="tile-info">
                  {t(frontFaceAligned ? "face-aligned" : "face-not-aligned")}
                </p>
              </div>
              <div
                className={`tile ${lightQuality === "good" ? "success" : ""}`}
              >
                <h4 className="tile-title">{t("face-light")}</h4>
                <p className="tile-info">{t(`light-${lightQuality}`)}</p>
              </div>
            </div>
          </div>
        </>
      )}
      {cameraLoaded && !showGuidanceOverlay && scanStep === 2 && (
        <div className={`webcam ${showCaptureFlash ? "flash" : ""}`} />
      )}
      {cameraLoaded && !showGuidanceOverlay && isTablet && scanStep === 1 && (
        <div className="scan-grid-zoom-button-container">
          {zoomLevels.map((zoom, index) => (
            <button
              key={index}
              className={`zoom-button ${zoomLevel === zoom && "zoom-button-selected"}`}
              onClick={() => cropWebCam(zoom)}
            >
              {`${zoom}${zoomLevel === zoom ? "x" : ""}`}
            </button>
          ))}
        </div>
      )}
      {cameraLoaded && !showGuidanceOverlay && (
        <>
          <div className="scan-grid-bottom-bg"></div>
          <div className="scan-grid-bottom">
            <p className="white-text">
              {scanStep === 1 ? t("facePosition") : t("faceSides")}
            </p>
          </div>
        </>
      )}
    </>
  );
};
